/**
 * Who is in the group, adding somebody who is not, and the links that let
 * somebody in.
 *
 * **Adding somebody takes a name and nothing else.** That is the whole point
 * of a placeholder: the fifth person at the table is at the table, and asking
 * for their email before the bill can be split five ways is asking for the
 * hard half at the worst moment — so what people do instead is split it four
 * ways and remember, which is the thing this app exists to stop. A name makes
 * a real member of the ledger, and the link that hands them their row comes
 * later, from here.
 *
 * **The token is shown once, because it only exists once.** The server stores
 * its SHA-256 and returns the link in the response that made it; nothing,
 * including this API, can produce it again. So the link is put on the screen
 * the moment it comes back, in a field that is already selected, and the list
 * below can only ever say that a link exists — never what it is. A screen that
 * quietly relied on being able to fetch it later would be a screen that
 * silently lost somebody's invite.
 */

import { useCallback, useState, type FormEvent } from 'react'
import type { Member } from '../api'
import { Avatar } from './Avatar'
import { useAddPlaceholder, useCreateInvite, useInvites, useRevokeInvite } from '../queries'

/** A link is only worth showing while it is still one. */
function describeInvite(expiresAt: string, uses: number, maxUses: number | null): string {
  const when = expiresAt.slice(0, 10)
  if (maxUses === null) return `open link · ${uses} used · expires ${when}`
  return `${uses} of ${maxUses} used · expires ${when}`
}

export function People({
  groupId,
  members,
  archived,
}: {
  groupId: string
  members: readonly Member[]
  archived: boolean
}) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  /**
   * The link that has just been minted, held here because there is nowhere
   * else it can be held. It is keyed by the member it claims so that minting a
   * second one for somebody else replaces the box rather than adding to it.
   */
  const [link, setLink] = useState<{ forUserId: string | null; url: string } | null>(null)

  const invites = useInvites(groupId, open)
  const addPlaceholder = useAddPlaceholder(groupId)
  const createInvite = useCreateInvite(groupId)
  const revokeInvite = useRevokeInvite(groupId)

  const current = members.filter((member) => member.leftAt === null)

  const add = useCallback(
    async (event: FormEvent) => {
      event.preventDefault()
      const trimmed = name.trim()
      if (!trimmed) return
      await addPlaceholder.mutateAsync(trimmed)
      setName('')
    },
    [addPlaceholder, name],
  )

  const mint = useCallback(
    async (placeholderUserId: string | null) => {
      const made = await createInvite.mutateAsync(
        placeholderUserId === null ? {} : { placeholderUserId },
      )
      setLink({ forUserId: placeholderUserId, url: new URL(made.url, window.location.origin).href })
    },
    [createInvite],
  )

  return (
    <div className="people-panel">
      <p className="actions">
        <button type="button" className="button--quiet" onClick={() => setOpen(!open)}>
          {open ? 'Hide people' : `People (${current.length})`}
        </button>
      </p>

      {open && (
        <>
          <ul className="people">
            {current.map((member) => (
              <li key={member.userId} className="person">
                <Avatar userId={member.userId} displayName={member.displayName} />
                <span className="person__who">
                  <span className="person__name">
                    {member.displayName}
                    {member.isPlaceholder && (
                      <span className="pill pill--pending">No account yet</span>
                    )}
                    {member.role === 'owner' && <span className="pill">Owner</span>}
                  </span>
                </span>
                {/*
                  Only a placeholder has anything to send. Everybody else is
                  already the person their row says they are, and a link that
                  handed their account to whoever held it would be the bug this
                  whole feature is careful not to be.
                */}
                {member.isPlaceholder && (
                  <button
                    type="button"
                    className="button--quiet"
                    disabled={archived || createInvite.isPending}
                    onClick={() => void mint(member.userId)}
                  >
                    Invite link
                    <span className="visually-hidden"> for {member.displayName}</span>
                  </button>
                )}
              </li>
            ))}
          </ul>

          {link && (
            <p className="note">
              <label>
                {link.forUserId === null
                  ? 'A link into this group. It works once.'
                  : 'Send this to them. It works once, and it is the only copy — it cannot be shown again.'}
                {/*
                  Focused and selected on arrival, because it is the one thing
                  on the screen the person pressed the button to get, and it is
                  the only time they will be able to get it.
                */}
                <input
                  readOnly
                  autoFocus
                  className="invite-link"
                  value={link.url}
                  onFocus={(event) => event.currentTarget.select()}
                />
              </label>
            </p>
          )}

          <form className="actions actions--tight" onSubmit={add}>
            <input
              name="displayName"
              aria-label="Their name"
              placeholder="Add somebody by name"
              value={name}
              disabled={archived}
              onChange={(event) => setName(event.target.value)}
            />
            <button type="submit" disabled={archived || addPlaceholder.isPending || !name.trim()}>
              Add
            </button>
            <button
              type="button"
              className="button--quiet"
              disabled={archived || createInvite.isPending}
              onClick={() => void mint(null)}
            >
              Link to join
            </button>
          </form>
          <p className="muted note">
            Somebody added by name is in the ledger straight away — split with them tonight, send
            them a link whenever. Following it hands them this exact row, and moves no balance.
          </p>

          {addPlaceholder.isError && <p className="bad note">{addPlaceholder.error.message}</p>}
          {createInvite.isError && <p className="bad note">{createInvite.error.message}</p>}
          {revokeInvite.isError && <p className="bad note">{revokeInvite.error.message}</p>}

          {invites.data && invites.data.invites.length > 0 && (
            <>
              <h3>Links waiting to be followed</h3>
              <ul className="people">
                {invites.data.invites.map((invite) => (
                  <li key={invite.id} className="person">
                    <span className="person__who">
                      <span className="person__name">
                        {invite.placeholderDisplayName ?? 'Anybody with the link'}
                        {invite.kind === 'claim' && <span className="pill">Claim</span>}
                      </span>
                      <span className="person__groups">
                        {describeInvite(invite.expiresAt, invite.uses, invite.maxUses)}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="button--quiet"
                      disabled={revokeInvite.isPending}
                      onClick={() => revokeInvite.mutate(invite.id)}
                    >
                      Revoke
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  )
}
